import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

const pricingPlans = [
  {
    name: 'Indie Filmmaker',
    price: '$0',
    cadence: 'for up to 2 productions',
    description: 'Ideal for indie crews getting a feature or short off the ground without budget friction.',
    highlights: [
      'Up to 2 active projects',
      'Unlimited collaborators',
      'Scene breakdowns & shot lists',
      'Task boards and schedule exports',
      'Email support'
    ],
    ctaText: 'Start Free',
    ctaTo: '/register'
  },
  {
    name: 'Studio',
    price: '$20',
    cadence: 'per month',
    description: 'Unlock AI tooling and unlimited productions for studios ready to scale workflows.',
    highlights: [
      'Unlimited active projects',
      'AI logline + insight tools included',
      'Advanced scheduling & call sheets',
      'Shared asset library with versioning',
      'Priority chat support'
    ],
    featured: true,
    ctaText: 'Upgrade to Studio',
    ctaTo: '/register'
  },
  {
    name: 'Student Access',
    price: '$0',
    cadence: 'with academic verification',
    description: 'Full platform access for accredited students building their reel or thesis projects.',
    highlights: [
      'Unlimited projects & collaborators',
      'AI tools and analytics included',
      'Production calendars & call sheets',
      'Asset storage for coursework',
      'Verification with .edu or program ID'
    ],
    ctaText: 'Claim Student Access',
    ctaTo: '/register'
  }
];

const featureCallouts = [
  {
    title: 'Keep every department aligned',
    body: 'Build production calendars, assign tasks, and track approvals in one shared workspace designed for Film & TV teams.'
  },
  {
    title: 'Unlock creative momentum',
    body: 'AI-assisted loglines, treatments, and script breakdowns reduce busywork so your crew focuses on the story.'
  },
  {
    title: 'Designed for real-world shoots',
    body: 'Robust permissions, offline call sheets, and vendor exports keep the set moving no matter the schedule.'
  }
];

function Pricing() {
  const theme = useTheme();

  useEffect(() => {
    document.title = 'Pricing • Luminara';
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${theme.palette.background.default} 45%)`,
        py: { xs: 8, md: 12 }
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={6}>
          <Box textAlign="center">
            <Chip
              icon={<StarRoundedIcon />}
              color="primary"
              label="Flexible plans for every production"
              sx={{ mb: 3, fontWeight: 600 }}
            />
            <Typography variant="h2" fontWeight={800} gutterBottom>
              Transparent pricing crafted for storytellers
            </Typography>
            <Typography variant="h6" color="text.secondary" maxWidth={720} margin="0 auto">
              Whether you are planning your first short or coordinating a multi-season series, Luminara scales with your workflow.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 999,
                  px: 3.5,
                  py: 0.85,
                  boxShadow: '0 6px 24px rgba(33,150,243,0.28)'
                }}
              >
                Create free account
              </Button>
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 0.75,
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderColor: theme.palette.primary.main
                  }
                }}
              >
                Back to landing
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={4}>
            {pricingPlans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.name}>
                <Card
                  elevation={plan.featured ? 6 : 2}
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    height: '100%',
                    border: plan.featured
                      ? `2px solid ${alpha(theme.palette.primary.main, 0.4)}`
                      : `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    transform: plan.featured ? 'translateY(-8px)' : 'none',
                    background: plan.featured
                      ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                      : theme.palette.background.paper
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Stack spacing={3}>
                      <Box>
                        <Typography variant="overline" color="primary" fontWeight={700} gutterBottom>
                          {plan.name}
                        </Typography>
                        <Typography variant="h3" fontWeight={800}>
                          {plan.price}
                          {plan.cadence && (
                            <Typography component="span" variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
                              {plan.cadence}
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
                          {plan.description}
                        </Typography>
                      </Box>

                      <Divider />

                      <List disablePadding>
                        {plan.highlights.map((highlight) => (
                          <ListItem key={highlight} sx={{ px: 0, py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleRoundedIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={highlight} primaryTypographyProps={{ fontWeight: 600 }} />
                          </ListItem>
                        ))}
                      </List>

                      <Button
                        component={RouterLink}
                        to={plan.ctaTo}
                        variant={plan.featured ? 'contained' : 'outlined'}
                        size="large"
                        color="primary"
                        sx={{ borderRadius: 3, fontWeight: 700 }}
                        fullWidth
                      >
                        {plan.ctaText}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={4}>
            {featureCallouts.map((feature) => (
              <Grid item xs={12} md={4} key={feature.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7)
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Stack spacing={2}>
                      <Typography variant="h5" fontWeight={700}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.body}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              borderRadius: 4,
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Stack spacing={3} alignItems="center">
              <Typography variant="h4" fontWeight={800}>
                Ready to roll cameras?
              </Typography>
              <Typography variant="body1" color="text.secondary" maxWidth={640}>
                Join thousands of film and television professionals already building their productions in Luminara. Start free or book a walk-through to tailor the platform to your studio.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  sx={{ borderRadius: 3, px: 5 }}
                >
                  Create Free Account
                </Button>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="outlined"
                  size="large"
                  sx={{ borderRadius: 3, px: 5 }}
                >
                  Talk to Sales
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export default Pricing;
